import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { listClients, createClient, PartnerClient } from "../../api/partner";

export default function PartnerClientsAll() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const {
    data: clients = [],
    isLoading,
    error,
  } = useQuery<PartnerClient[]>({
    queryKey: ["partner-clients"],
    queryFn: () => listClients(token!),
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
  });

  const createMutation = useMutation({
    mutationFn: (data: { client_name: string; contact_email?: string }) =>
      createClient(data, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-clients"] });
      setShowCreate(false);
      setNewClientName("");
      setNewContactEmail("");
      setCreateError(null);
    },
    onError: (err: Error) => {
      setCreateError(err.message);
    },
  });

  const handleCreate = () => {
    if (!newClientName.trim()) return;
    setCreateError(null);
    createMutation.mutate({
      client_name: newClientName.trim(),
      contact_email: newContactEmail.trim() || undefined,
    });
  };

  return (
    <div className="pt-0 px-6 pb-6">
      <div className="flex items-center justify-between mt-2 mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Clients</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-primary"
        >
          + New Client
        </button>
      </div>

      {/* Create Client Modal */}
      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowCreate(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-surface-700 border border-border p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-4 text-h4 text-text-primary">New Client</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-body-sm text-text-secondary mb-1">
                  Client Name *
                </label>
                <input
                  type="text"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  className="w-full rounded-lg bg-surface-600 border border-border px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-quantum-500"
                  placeholder="e.g. Acme Energy"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-body-sm text-text-secondary mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={newContactEmail}
                  onChange={(e) => setNewContactEmail(e.target.value)}
                  className="w-full rounded-lg bg-surface-600 border border-border px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-quantum-500"
                  placeholder="contact@acme-energy.com"
                />
              </div>

              {createError && (
                <p className="text-health-400 text-body-sm">{createError}</p>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                className="btn-secondary"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCreate}
                disabled={!newClientName.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create Client"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading / Error */}
      {isLoading && (
        <div className="text-text-secondary text-body-sm">Loading clients...</div>
      )}
      {error && (
        <div className="bg-red-50/10 border border-health-400/30 text-health-400 px-4 py-3 rounded">
          Error loading clients: {(error as Error).message}
        </div>
      )}

      {/* Client Cards */}
      {!isLoading && clients.length === 0 && (
        <div className="text-center py-12 text-text-secondary">
          <p className="text-lg mb-2">No clients yet</p>
          <p className="text-body-sm">
            Create your first client to start screening projects.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {clients.map((client) => (
          <div
            key={client.customer_id}
            onClick={() => navigate(`/app/clients/${client.customer_id}`)}
            className="cursor-pointer rounded-xl bg-surface-800 border border-border p-5 hover:border-quantum-500/50 transition-colors"
          >
            <h3 className="text-lg font-semibold text-text-primary mb-1">
              {client.client_name}
            </h3>
            {client.contact_email && (
              <p className="text-body-sm text-text-muted mb-3">
                {client.contact_email}
              </p>
            )}

            <div className="flex items-center gap-4 text-body-sm text-text-secondary">
              <span>{client.project_count} project{client.project_count !== 1 ? "s" : ""}</span>
              <span>{client.portfolio_count} portfolio{client.portfolio_count !== 1 ? "s" : ""}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
